import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { AppIcon } from "../components/AppIcon";
import { Icon } from "../components/Icon";
import { Page, TopBar } from "../components/Layout";
import { LoadMore } from "../components/ListControls";
import { discountNote, Price } from "../components/Price";
import { SubscriptionControls } from "../components/SubscriptionControls";
import { api } from "../lib/api";
import { checkoutClient, goToCheckout, waitForActivation } from "../lib/checkout";
import { load, save } from "../lib/storage";
import { useToast } from "../lib/toast";
import { type AppCard, type AppDetail as Detail, type InstallState } from "../lib/types";
import { usePaged } from "../lib/usePaged";
import { OmniBanner } from "./OmniPlans";

function PriceLabel({ a, end }: { a: AppCard; end?: boolean }) {
  const paid = a.plans.filter((p) => (p.list_price ?? p.price) > 0);
  if (paid.length === 0) return <>Free</>;
  const cheapest = paid.reduce((m, p) => (p.price < m.price ? p : m));
  return (
    <span style={{ display: "inline-flex", justifyContent: end ? "flex-end" : "flex-start" }}>
      <Price plan={cheapest} size="sm" prefix={a.plans.some((p) => (p.list_price ?? p.price) === 0) ? "Free, or" : undefined} />
    </span>
  );
}

export function Marketplace() {
  const [category, setCategory] = useState("");
  const list = usePaged<AppCard>("/api/marketplace", { sort: "name", extra: category ? { category } : {} });
  const [categories, setCategories] = useState<string[]>([]);
  useEffect(() => {
    api<{ items: AppCard[] }>("/api/marketplace", { query: { limit: 200 } }).then((p) => setCategories([...new Set(p.items.map((a) => a.category))].sort()));
  }, []);
  const [featured, ...rest] = list.items;

  return (
    <>
      <TopBar title="Marketplace" />
      <Page>
        <label className="search-box">
          <Icon name="search" size={20} />
          <input type="search" placeholder="Search apps" aria-label="Search apps" value={list.q} onChange={(e) => list.setQ(e.target.value)} />
        </label>
        {categories.length > 1 && (
          <div className="chips" role="radiogroup" aria-label="Category">
            {["", ...categories].map((c) => (
              <button key={c || "all"} role="radio" aria-checked={category === c} className={`chip${category === c ? " on" : ""}`} onClick={() => setCategory(c)}>
                {c || "All"}
              </button>
            ))}
          </div>
        )}
        {list.error && <p className="error">{list.error}</p>}
        {!list.q && !category && <OmniBanner />}

        {featured && (
          <Link to={`/marketplace/${featured.id}`} className="card featured">
            <div className="row">
              <AppIcon app={featured} size={56} />
              <div className="grow">
                <span className="d" style={{ fontSize: 20, fontWeight: 700, display: "block" }}>{featured.name}</span>
                <span className="small muted">by {featured.developer || "Omni"}</span>
              </div>
              {featured.installed ? <span className="pill ok">Installed</span> : !list.q && !category && <span className="pill jade">Featured</span>}
            </div>
            <p style={{ fontSize: 15, lineHeight: 1.45, color: "var(--omni-text-2)" }}>{featured.description}</p>
            <div className="row between">
              <span className="grow" style={{ fontSize: 14, fontWeight: 600 }}><PriceLabel a={featured} /></span>
              <span className="btn btn-primary btn-sm">{featured.installed ? "Open" : "View"}</span>
            </div>
          </Link>
        )}

        {rest.length > 0 && (
          <div className="card flush">
            {rest.map((a) => (
              <Link key={a.id} to={`/marketplace/${a.id}`} className="list-row" style={{ minHeight: 64 }}>
                <AppIcon app={a} size={44} />
                <span className="grow">
                  <strong style={{ fontWeight: 600 }}>{a.name}</strong>
                  <span className="sub clamp">{a.description}</span>
                </span>
                <span className="small muted">{a.installed ? "Installed" : <PriceLabel a={a} end />}</span>
              </Link>
            ))}
          </div>
        )}
        {!list.loading && list.total === 0 && <p className="muted center">No apps found.</p>}
        <LoadMore hasMore={list.hasMore} loading={list.loading} onClick={list.loadMore} shown={list.items.length} total={list.total} />

        <div className="card dashed row">
          <span className="grow">
            <strong style={{ fontWeight: 600 }}>Build an app for Omni</strong>
            <span className="sub">Publish a Vite app with the SDK. It appears here without an app-store update.</span>
          </span>
          <a className="link-btn" href="https://github.com/prototypeforge/everything/blob/main/docs/APP_SDK.md" target="_blank" rel="noreferrer">SDK guide</a>
        </div>
      </Page>
    </>
  );
}

const PERMISSION_ICON: Record<string, string> = { "profile:read": "profile", "profile:email": "mail", ai: "sparkle", files: "document" };

export function AppDetail() {
  const { appId } = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const toast = useToast();
  const [app, setApp] = useState<(Detail & { installation: InstallState | null }) | null>(null);
  const [plan, setPlan] = useState<string>("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Paying: waiting for Stripe to confirm (ADR-0033)
  const [waiting, setWaiting] = useState(search.get("checkout") === "success");

  const fetchApp = () => api<Detail & { installation: InstallState | null }>(`/api/marketplace/${appId}`);
  const reload = () =>
    fetchApp()
      .then((a) => {
        setApp(a);
        const buying = waiting ? load<string | null>(`omni.checkout.${appId}`, null) : null;
        const inst = a.installation;
        const onFree = !!inst?.entitled && (a.plans.find((p) => p.id === inst.plan_id)?.list_price ?? a.plans.find((p) => p.id === inst.plan_id)?.price ?? 0) === 0;
        const paid = a.plans.find((p) => (p.list_price ?? p.price) > 0);
        // On the free plan: show the paid one, so upgrading is one tap
        setPlan(buying ?? (onFree && paid ? paid.id : inst?.entitled ? inst.plan_id : paid?.id ?? a.plans[0]?.id ?? ""));
        return a;
      })
      .catch((e) => setError(e.message));

  useEffect(() => {
    if (!waiting || !appId) return;
    const buying = load<string | null>(`omni.checkout.${appId}`, null);
    return waitForActivation(async () => {
      const a = await fetchApp();
      setApp(a);
      // Done when the plan being bought is the one in use
      return !!a.installation?.entitled && (!buying || a.installation.plan_id === buying);
    }, () => {
      setWaiting(false);
      save(`omni.checkout.${appId}`, null);
      toast("Your plan is active", "success");
      void reload();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, appId]);

  useEffect(() => {
    void reload();
    if (search.get("checkout") === "cancel") toast("Payment cancelled. Nothing was charged.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const chosen = useMemo(() => app?.plans.find((p) => p.id === plan), [app, plan]);
  const back = (
    <button className="btn btn-ghost btn-sm" style={{ paddingLeft: 4 }} onClick={() => nav("/marketplace")}>
      <Icon name="chevron-left" size={20} />Marketplace
    </button>
  );
  if (!app)
    return (
      <>
        <TopBar title="" back={back} avatar={false} />
        <Page>{error ? <p className="error">{error}</p> : <p className="muted">Loading…</p>}</Page>
      </>
    );

  const inst = app.installation;
  const current = inst?.entitled && inst.plan_id === plan;
  const mine = inst?.entitled ? app.plans.find((p) => p.id === inst.plan_id) : undefined;

  const install = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ checkout_url: string | null }>(`/api/marketplace/${app.id}/install`, {
        body: { plan_id: plan, accept_permissions: accepted || app.permissions.length === 0 || !!inst, client: checkoutClient },
      });
      if (res.checkout_url) {
        save(`omni.checkout.${app.id}`, plan);
        await goToCheckout(res.checkout_url); // the web leaves the page; the app waits for the payment
        setWaiting(true);
        return;
      }
      toast(`${app.name} is on your space`, "success");
      nav(inst ? `/apps/${app.id}` : "/");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const uninstall = async () => {
    const wipe = confirm(`Remove ${app.name}? Press OK to also delete its data, Cancel to keep your data.`);
    await api(`/api/apps/${app.id}`, { method: "DELETE", query: { delete_data: wipe } });
    toast(`${app.name} removed`);
    void reload();
  };

  const cta = current
    ? `Open ${app.name}`
    : inst && chosen
      ? chosen.price > 0 && chosen.trial_days && !inst.subscription ? `Start ${chosen.trial_days}-day free trial of ${chosen.name}` : `Switch to ${chosen.name}`
      : chosen && chosen.price > 0
        ? chosen.trial_days
          ? `Start ${chosen.trial_days}-day free trial`
          : "Subscribe and install"
        : "Install";

  return (
    <>
      <TopBar title="" back={back} avatar={false} />
      <Page>
        <div className="row" style={{ gap: 16 }}>
          <AppIcon app={app} size={72} />
          <div className="stack" style={{ gap: 2 }}>
            <h1>{app.name}</h1>
            <span className="small muted">
              by {app.developer || "Omni"} · <span className="m">v{app.version}</span>
            </span>
          </div>
        </div>
        <p style={{ color: "var(--omni-text-2)" }}>{app.description}</p>

        {inst && (
          <div className="card stack">
            <div className="row between" style={{ gap: 12 }}>
              <span className="grow">
                <span className="small muted" style={{ display: "block" }}>Your plan</span>
                <strong style={{ fontSize: 17 }}>{mine?.name ?? (inst.status === "pending" ? "Waiting for payment" : "None")}</strong>
                {mine && <span className="small muted"> · {mine.price > 0 ? <Price plan={mine} size="sm" /> : "Free"}</span>}
              </span>
              {inst.entitled && <button className="btn btn-sm btn-primary" onClick={() => nav(`/apps/${app.id}`)}>Open</button>}
            </div>
            <SubscriptionControls sub={inst.subscription} name={mine?.name ?? app.name} onChange={() => void reload()} />
          </div>
        )}

        <div className="card stack">
          {inst && app.plans.length > 1 && <span className="lbl" style={{ padding: 0 }}>{mine && mine.price === 0 ? "Upgrade" : "All plans"}</span>}
          {app.plans.length > 1 && (
            <div className="segmented" role="radiogroup" aria-label="Plan">
              {app.plans.map((p) => (
                <button key={p.id} role="radio" aria-checked={p.id === plan} className={p.id === plan ? "active" : ""} onClick={() => setPlan(p.id)}>
                  {p.name}
                </button>
              ))}
            </div>
          )}
          {chosen && (
            <>
              <div className="row between" style={{ alignItems: "baseline" }}>
                <Price plan={chosen} per={chosen.price > 0} />
                {chosen.price > 0 && <span className="small muted">Cancel anytime</span>}
              </div>
              {discountNote(chosen) && <span className="small" style={{ color: "var(--omni-accent)" }}>{discountNote(chosen)}</span>}
              {chosen.features.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: "var(--omni-text-2)" }}>
                  {chosen.features.map((f) => <li key={f}>{f}</li>)}
                </ul>
              )}
            </>
          )}
          {!inst && app.permissions.length > 0 && (
            <label className="row small" style={{ minHeight: 44 }}>
              <input type="checkbox" style={{ width: 20, height: 20, accentColor: "var(--omni-accent)" }} checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
              I allow {app.name} the access listed below
            </label>
          )}
          {waiting && !current ? (
            <div className="checkout-wait" role="status"><span className="spinner" aria-hidden />Waiting for the payment to be confirmed…
              <button className="link-btn" onClick={() => setWaiting(false)}>Hide</button>
            </div>
          ) : (
            <button
              className="btn btn-primary btn-block"
              disabled={busy || (!inst && app.permissions.length > 0 && !accepted)}
              onClick={current ? () => nav(`/apps/${app.id}`) : install}
            >
              {busy ? "Working…" : cta}
            </button>
          )}
          {chosen && chosen.price > 0 && <span className="xs muted">Paid securely with Stripe.{chosen.trial_days ? ` No charge during the ${chosen.trial_days}-day trial.` : ""}</span>}
          {error && <p className="error small" role="alert">{error}</p>}
        </div>

        {app.permissions.length > 0 && (
          <>
            <span className="lbl">What {app.name} can access</span>
            <div className="card flush">
              {app.permissions.map((p) => (
                <div key={p.id} className="perm">
                  <Icon name={PERMISSION_ICON[p.id] ?? "transactions"} size={20} />
                  <span style={{ fontSize: 15 }}>{p.description}</span>
                </div>
              ))}
              {app.collections.filter((c) => c.shared).length > 0 && (
                <div className="perm">
                  <Icon name="shield" size={20} />
                  <span className="stack" style={{ gap: 2 }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>Your {app.name.toLowerCase()} data</span>
                    <span className="small muted">
                      {app.collections.filter((c) => c.shared).map((c) => c.description || c.name).join(", ")}. Other apps see it only if you allow it.
                    </span>
                  </span>
                </div>
              )}
            </div>
          </>
        )}
        {inst && (
          <button className="btn btn-ghost danger" onClick={uninstall}>
            <Icon name="trash" size={20} />Remove {app.name}
          </button>
        )}
      </Page>
    </>
  );
}
